import React, { useState, useMemo } from 'react';
import { Search, User, CheckCircle2 } from 'lucide-react';

interface Worker {
  id: string;
  name: string;
  role: string;
  email: string;
  whitecard_number?: string;
  white_card_number?: string;
  certifications?: string[];
  status: string;
}

interface WorkerSelectionPanelProps {
  workers: Worker[];
  selectedWorkerIds: Set<string>;
  onToggleWorker: (workerId: string) => void;
  documentType: string;
  siteId?: string;
  siteWorkerIds?: Set<string>;
}

export default function WorkerSelectionPanel({
  workers,
  selectedWorkerIds,
  onToggleWorker,
  documentType,
  siteWorkerIds = new Set(),
}: WorkerSelectionPanelProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'active' | 'site' | 'certified'>('all');

  const filteredWorkers = useMemo(() => {
    return workers.filter(worker => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          worker.name.toLowerCase().includes(query) ||
          worker.email.toLowerCase().includes(query) ||
          worker.role.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }

      if (filterType === 'active' && worker.status !== 'active') return false;
      if (filterType === 'site' && !siteWorkerIds.has(worker.id)) return false;
      if (filterType === 'certified') {
        const certs = worker.certifications || [];
        if (certs.length === 0) return false;
      }

      return true;
    });
  }, [workers, searchQuery, filterType, siteWorkerIds]);

  const handleSelectFromSite = () => {
    siteWorkerIds.forEach(workerId => {
      if (!selectedWorkerIds.has(workerId)) {
        onToggleWorker(workerId);
      }
    });
  };

  const handleClearSelection = () => {
    selectedWorkerIds.forEach(workerId => {
      onToggleWorker(workerId);
    });
  };

  const getDocumentInfo = (docType: string) => {
    switch (docType) {
      case 'swms':
        return {
          name: 'Asbestos SWMS',
          color: 'blue',
          bgClass: 'bg-blue-500',
          borderClass: 'border-blue-500',
          textClass: 'text-blue-800',
          lightBgClass: 'bg-blue-50',
        };
      case 'arcp':
        return {
          name: 'Asbestos Removal Control Plan',
          color: 'green',
          bgClass: 'bg-green-500',
          borderClass: 'border-green-500',
          textClass: 'text-green-800',
          lightBgClass: 'bg-green-50',
        };
      case 'demo':
        return {
          name: 'Demolition SWMS',
          color: 'red',
          bgClass: 'bg-red-500',
          borderClass: 'border-red-500',
          textClass: 'text-red-800',
          lightBgClass: 'bg-red-50',
        };
      default:
        return {
          name: 'Document',
          color: 'gray',
          bgClass: 'bg-gray-500',
          borderClass: 'border-gray-500',
          textClass: 'text-gray-800',
          lightBgClass: 'bg-gray-50',
        };
    }
  };

  const docInfo = getDocumentInfo(documentType);

  return (
    <div className={`mt-4 rounded-lg border-2 overflow-hidden ${docInfo.borderClass}`}>
      <div className={`${docInfo.bgClass} px-4 py-3`}>
        <div className="flex items-center justify-between">
          <h4 className="font-bold text-white">
            {docInfo.name}
          </h4>
          <span className="text-sm font-semibold text-white bg-white bg-opacity-20 px-3 py-1 rounded-full">
            {selectedWorkerIds.size} workers selected
          </span>
        </div>
      </div>

      <div className="p-4 bg-gray-50">
      <div className="flex items-center justify-between mb-3">
        <h5 className="font-semibold text-sm text-gray-900">
          Select Workers for This Document
        </h5>
      </div>

      <div className="mb-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search workers by name, email, or role..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <button
          onClick={() => setFilterType('all')}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
            filterType === 'all'
              ? 'bg-orange-500 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          All
        </button>
        <button
          onClick={() => setFilterType('active')}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
            filterType === 'active'
              ? 'bg-orange-500 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Active Only
        </button>
        <button
          onClick={() => setFilterType('site')}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
            filterType === 'site'
              ? 'bg-orange-500 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          On Selected Site
        </button>
        <button
          onClick={() => setFilterType('certified')}
          className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
            filterType === 'certified'
              ? 'bg-orange-500 text-white'
              : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
          }`}
        >
          Certified
        </button>
      </div>

      <div className="flex gap-2 mb-3">
        {siteWorkerIds.size > 0 && (
          <button
            onClick={handleSelectFromSite}
            className="px-3 py-1.5 text-xs font-medium text-orange-700 bg-orange-100 rounded-lg hover:bg-orange-200 transition-colors"
          >
            Select All from Site
          </button>
        )}
        {selectedWorkerIds.size > 0 && (
          <button
            onClick={handleClearSelection}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Clear Selection
          </button>
        )}
      </div>

      <div className="max-h-64 overflow-y-auto space-y-2">
        {filteredWorkers.length === 0 ? (
          <div className="text-center py-8 text-gray-500 text-sm">
            No workers found matching your criteria
          </div>
        ) : (
          filteredWorkers.map(worker => {
            const isSelected = selectedWorkerIds.has(worker.id);
            const isOnSite = siteWorkerIds.has(worker.id);
            const whiteCard = worker.whitecard_number || worker.white_card_number;

            return (
              <button
                key={worker.id}
                onClick={() => onToggleWorker(worker.id)}
                className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                  isSelected
                    ? `${docInfo.borderClass} ${docInfo.lightBgClass}`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={`mt-0.5 w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected
                        ? `${docInfo.bgClass} ${docInfo.borderClass}`
                        : 'border-gray-300 bg-white'
                    }`}
                  >
                    {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="font-semibold text-sm text-gray-900 truncate">
                        {worker.name}
                      </span>
                      {isOnSite && (
                        <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full flex-shrink-0">
                          On Site
                        </span>
                      )}
                    </div>

                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">Role:</span>
                        <span>{worker.role}</span>
                      </div>
                      {whiteCard && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium">White Card:</span>
                          <span className="font-mono">{whiteCard}</span>
                        </div>
                      )}
                      {worker.certifications && worker.certifications.length > 0 && (
                        <div className="flex items-start gap-2">
                          <span className="font-medium flex-shrink-0">Certifications:</span>
                          <div className="flex flex-wrap gap-1">
                            {worker.certifications.map((cert, idx) => (
                              <span
                                key={idx}
                                className="px-1.5 py-0.5 text-xs bg-green-100 text-green-800 rounded"
                              >
                                {cert}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>
      </div>
    </div>
  );
}
